"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import { dmsApi } from '@/lib/api';
import { 
  Folder, 
  File, 
  ChevronRight, 
  MoreVertical, 
  Download, 
  Trash2, 
  RotateCcw, 
  Search,
  ArrowLeft,
  FileText,
  FileArchive,
  FileImage,
  Loader2,
  FolderPlus,
  Upload,
  Pencil,
  MoveHorizontal,
  Eye,
  X,
  Share2,
  Lock,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { ShareResourceDialog } from '@/components/sharing/share-resource-dialog';

export interface ShareBrowseContext {
  rootFolderId: string;
  permissionLevel: 'view' | 'edit';
}

interface FileBrowserProps {
  initialFolderId?: string;
  viewMode?: 'all' | 'trash';
  /** When set, user opened a folder via “Shared with me”; limit edit/delete UI per share level. */
  shareBrowseContext?: ShareBrowseContext | null;
  /** Open file preview after loading metadata (from Shared with me). */
  openSharedFile?: { fileId: string; permissionLevel: 'view' | 'edit' } | null;
  onOpenSharedFileDone?: () => void;
  /** Called when user exits shared browse mode from the shared root folder. */
  onExitSharedBrowse?: () => void;
}

function resourceDepartmentId(entity: { departmentId?: string | { _id: string } } | null | undefined): string {
  const d = entity?.departmentId;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && d._id) return String(d._id);
  return '';
}

const PRIVATE_FOLDER_ACCESS_ERROR = 'Access denied: private folder';
const PRIVATE_FOLDER_FRIENDLY_TEXT = 'This folder is private. Ask owner to share it.';

function isPrivateFolderAccessDenied(err: unknown): boolean {
  const e = err as { status?: number; error?: string; message?: string };
  const msg = (e?.error ?? e?.message ?? '').toString().toLowerCase();
  return e?.status === 403 && msg.includes(PRIVATE_FOLDER_ACCESS_ERROR.toLowerCase());
}

function getFolderActionErrorMessage(err: unknown, fallback: string): string {
  if (isPrivateFolderAccessDenied(err)) return PRIVATE_FOLDER_FRIENDLY_TEXT;
  const e = err as { error?: string; message?: string; statusText?: string };
  const raw = e?.error ?? e?.message ?? (typeof e?.statusText === 'string' ? e.statusText : '');
  if (raw && String(raw).toLowerCase().includes('fetch')) {
    return 'Network error. Check your connection and that the app is using the API proxy (see next.config rewrites).';
  }
  return raw || fallback;
}

export function FileBrowser({
  initialFolderId,
  viewMode = 'all',
  shareBrowseContext = null,
  openSharedFile = null,
  onOpenSharedFileDone,
  onExitSharedBrowse,
}: FileBrowserProps) {
  const [currentFolder, setCurrentFolder] = useState<any>(null);
  const [children, setChildren] = useState<{ folders: any[]; files: any[] }>({ folders: [], files: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState<string | null>(null);
  const [globalSearchResults, setGlobalSearchResults] = useState<{ folders: any[]; files: any[] } | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  /** Full path from root to current folder for breadcrumb (each has _id and name) */
  const [pathFolders, setPathFolders] = useState<Array<{ _id: string; name: string }>>([]);
  const { hasPermission, user, isSuperAdmin } = useAuth();
  
  const [uploadProgress, setUploadProgress] = useState<{ name: string; percent: number } | null>(null);
  
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderIsPrivate, setNewFolderIsPrivate] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [renameItem, setRenameItem] = useState<{ id: string, name: string, type: 'folder' | 'file' } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  
  const [moveSubmitting, setMoveSubmitting] = useState<string | null>(null);
  
  const [draggedFileId, setDraggedFileId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  
  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, visible: boolean } | null>(null);

  /** File currently shown in preview popup */
  const [previewFile, setPreviewFile] = useState<{ _id: string; originalName: string; mimeType?: string } | null>(null);
  const [previewTextContent, setPreviewTextContent] = useState<string | null>(null);
  const [previewTextLoading, setPreviewTextLoading] = useState(false);
  const [centerError, setCenterError] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const globalSearchReqIdRef = useRef(0);

  const inSharedRecipientMode = Boolean(shareBrowseContext && viewMode === 'all');
  const shareCanEdit = shareBrowseContext?.permissionLevel === 'edit';

  const canEditFilesInFolder =
    !inSharedRecipientMode ? hasPermission('update_file') : shareCanEdit;

  const canUploadInFolder =
    !inSharedRecipientMode ? hasPermission('upload_file') : shareCanEdit;

  const canCreateSubfolder =
    !inSharedRecipientMode
      ? hasPermission('create_folder')
      : shareCanEdit && hasPermission('create_folder');

  const isDepartmentRootFolder = Boolean(
    currentFolder &&
      !inSharedRecipientMode &&
      (
        currentFolder?.isRoot === true ||
        currentFolder?.parentId == null ||
        (pathFolders.length === 1 && currentFolder?._id === pathFolders[0]?._id)
      )
  );

  const canUploadInCurrentFolder = canUploadInFolder && !isDepartmentRootFolder;

  const canRenameFolder =
    !inSharedRecipientMode || shareCanEdit;

  const showDeleteFolder =
    viewMode === 'all' && !inSharedRecipientMode && hasPermission('delete_folder');
  const showDeleteFile =
    viewMode === 'all' && !inSharedRecipientMode && hasPermission('delete_file');

  const userDepartmentIdStr = (() => {
    const d = user?.departmentId as unknown;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && '_id' in (d as object))
      return String((d as { _id: string })._id);
    return '';
  })();

  const canManageSharesOnDept = (deptId: string) => {
    if (!deptId) return false;
    if (isSuperAdmin) return true;
    if (!userDepartmentIdStr) return false;
    return userDepartmentIdStr === String(deptId);
  };

  const currentUserId = (user as any)?.id ?? (user as any)?._id ?? '';

  const canManageSharesOnResource = (resource: any) => {
    if (!resource) return false;
    if (isSuperAdmin) return true;
    const visibility = String(resource?.visibility ?? '').toLowerCase();
    if (visibility === 'private') {
      const ownerUserId = String(resource?.ownerUserId ?? '');
      return Boolean(ownerUserId) && ownerUserId === String(currentUserId);
    }
    return canManageSharesOnDept(resourceDepartmentId(resource));
  };

  const [shareTarget, setShareTarget] = useState<
    null | { resourceType: 'file' | 'folder'; resourceId: string }
  >(null);

  const fetchFolderData = useCallback(
    async (
      folderId: string,
      options?: { pathMode?: 'replace' | 'append' | 'none'; silent?: boolean }
    ) => {
      if (!options?.silent) setIsLoading(true);
      try {
        const folder = await dmsApi.folders.get(folderId);
        const data = await dmsApi.folders.getChildren(folderId, viewMode === 'trash');
        setCurrentFolder(folder);
        setChildren(data);
        const pathMode = options?.pathMode ?? 'none';
        if (pathMode === 'replace') {
          setPathFolders([{ _id: folder._id, name: folder.name }]);
        } else if (pathMode === 'append') {
          setPathFolders((prev) => [...prev, { _id: folder._id, name: folder.name }]);
        }
      } catch (err: any) {
        const message = getFolderActionErrorMessage(err, 'Failed to load folder.');
        if (message === "Folder not found") {
          // Show centered alert instead of toast when user tries to access a department they don't belong to
          setCenterError("You are not belong to this department.");
        } else if (isPrivateFolderAccessDenied(err)) {
          setCenterError(PRIVATE_FOLDER_FRIENDLY_TEXT);
        } else {
          toast({ variant: "destructive", title: "Error", description: message });
        }
      } finally {
        if (!options?.silent) setIsLoading(false);
      }
    },
    [viewMode]
  );

  useEffect(() => {
    if (initialFolderId) {
      fetchFolderData(initialFolderId, { pathMode: 'replace' });
    } else {
      setPathFolders([]);
      setCurrentFolder(null);
      setChildren({ folders: [], files: [] });
      setIsLoading(false);
    }
  }, [initialFolderId, fetchFolderData]);

  useEffect(() => {
    const fid = openSharedFile?.fileId;
    if (!fid || viewMode !== 'all') return;
    let cancelled = false;
    (async () => {
      try {
        const f = await dmsApi.files.get(fid);
        if (cancelled) return;
        setPreviewFile({
          _id: f._id,
          originalName: f.originalName ?? f.name ?? 'File',
          mimeType: f.mimeType,
        });
      } catch (err: unknown) {
        const e = err as { error?: string; message?: string; status?: number };
        toast({
          variant: 'destructive',
          title: 'Error',
          description: isPrivateFolderAccessDenied(err)
            ? PRIVATE_FOLDER_FRIENDLY_TEXT
            : e?.error ?? e?.message ?? 'Could not open file.',
        });
      } finally {
        if (!cancelled) onOpenSharedFileDone?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openSharedFile?.fileId, viewMode, onOpenSharedFileDone]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // Radix DismissableLayer sets body.style.pointerEvents = 'none' when dialog opens but never restores it
  // (cleanup checks size===1 after another effect already removed the node). Restore body and remove any
  // block-interactivity-* classes (e.g. from react-remove-scroll).
  const clearBodyDialogStyles = useCallback(() => {
    if (typeof document === 'undefined') return;
    const restore = () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = '';
      // Remove react-remove-scroll / aria-hidden style classes that set pointer-events: none on body
      const list = document.body.classList;
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].startsWith('block-interactivity-')) list.remove(list[i]);
      }
    };
    restore();
    const t0 = setTimeout(restore, 0);
    const t1 = setTimeout(restore, 100);
    const t2 = setTimeout(restore, 300);
    return () => { clearTimeout(t0); clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Safety net: whenever no modal is open, re-apply body restore after a delay so we run after Radix cleanup.
  useEffect(() => {
    if (isRenameOpen || previewFile || isNewFolderOpen || Boolean(shareTarget) || Boolean(centerError)) return;
    const t1 = setTimeout(() => {
      if (typeof document === 'undefined') return;
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = '';
      const list = document.body.classList;
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].startsWith('block-interactivity-')) list.remove(list[i]);
      }
    }, 150);
    const t2 = setTimeout(() => {
      if (typeof document === 'undefined') return;
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = '';
    }, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isRenameOpen, previewFile, isNewFolderOpen, shareTarget, centerError]);

  // When preview opens for a text-type file, fetch content
  useEffect(() => {
    if (!previewFile) {
      setPreviewTextContent(null);
      return;
    }
    const mime = (previewFile.mimeType || '').toLowerCase();
    const isTextLike =
      mime.startsWith('text/') ||
      mime === 'application/json' ||
      mime === 'application/xml' ||
      mime === 'application/javascript' ||
      mime === 'application/x-yaml' ||
      mime === 'application/yaml';
    if (!isTextLike) {
      setPreviewTextContent(null);
      return;
    }
    setPreviewTextLoading(true);
    setPreviewTextContent(null);
    const url = dmsApi.files.getFileViewUrl(previewFile._id);
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('Failed to load'))))
      .then((text) => {
        setPreviewTextContent(text);
      })
      .catch(() => {
        setPreviewTextContent(null);
      })
      .finally(() => {
        setPreviewTextLoading(false);
      });
  }, [previewFile?._id]);

  const refreshData = (silent = false) => {
    if (currentFolder?._id) fetchFolderData(currentFolder._id, silent ? { pathMode: 'none', silent: true } : undefined);
  };

  const clearGlobalSearch = useCallback(() => {
    setGlobalSearchResults(null);
    setGlobalSearchError(null);
  }, []);

  const searchDepartmentTree = useCallback(
    async (
      folderId: string,
      queryLower: string,
      acc: { folders: any[]; files: any[] },
      visited: Set<string>,
      maxResults = 400
    ) => {
      if (!folderId || visited.has(folderId)) return;
      if (acc.folders.length + acc.files.length >= maxResults) return;
      visited.add(folderId);
      const data = await dmsApi.folders.getChildren(folderId, false);
      const folders = Array.isArray(data?.folders) ? data.folders : [];
      const files = Array.isArray(data?.files) ? data.files : [];

      for (const folder of folders) {
        if (acc.folders.length + acc.files.length >= maxResults) break;
        const name = String(folder?.name ?? '').toLowerCase();
        if (name.includes(queryLower)) acc.folders.push(folder);
      }

      for (const file of files) {
        if (acc.folders.length + acc.files.length >= maxResults) break;
        const name = String(file?.originalName ?? file?.name ?? '').toLowerCase();
        if (name.includes(queryLower)) acc.files.push(file);
      }

      for (const folder of folders) {
        if (acc.folders.length + acc.files.length >= maxResults) break;
        await searchDepartmentTree(folder._id, queryLower, acc, visited, maxResults);
      }
    },
    []
  );

  const handleGlobalSearch = useCallback(async () => {
    const query = globalSearchQuery.trim();
    if (!query) {
      clearGlobalSearch();
      return;
    }
    if (query.length < 2) {
      toast({
        variant: 'destructive',
        title: 'Search',
        description: 'Type at least 2 characters for global department search.',
      });
      return;
    }

    const rootFolderId = pathFolders[0]?._id || currentFolder?._id;
    if (!rootFolderId) return;

    const reqId = ++globalSearchReqIdRef.current;
    setGlobalSearchLoading(true);
    setGlobalSearchError(null);
    try {
      const acc = { folders: [] as any[], files: [] as any[] };
      await searchDepartmentTree(rootFolderId, query.toLowerCase(), acc, new Set<string>());
      if (reqId !== globalSearchReqIdRef.current) return;
      setGlobalSearchResults(acc);
    } catch (err: any) {
      if (reqId !== globalSearchReqIdRef.current) return;
      setGlobalSearchResults({ folders: [], files: [] });
      setGlobalSearchError(err?.error ?? err?.message ?? 'Global search failed.');
    } finally {
      if (reqId === globalSearchReqIdRef.current) setGlobalSearchLoading(false);
    }
  }, [globalSearchQuery, clearGlobalSearch, pathFolders, currentFolder?._id, searchDepartmentTree]);

  const isAtSharedBrowseRoot =
    inSharedRecipientMode &&
    Boolean(shareBrowseContext?.rootFolderId) &&
    currentFolder?._id === shareBrowseContext?.rootFolderId;

  const canGoBack = pathFolders.length > 1 || isAtSharedBrowseRoot;

  const navigateTo = (folderId: string) => {
    if (globalSearchResults) clearGlobalSearch();
    if (currentFolder) setHistory((prev) => [...prev, currentFolder._id]);
    fetchFolderData(folderId, { pathMode: 'append' });
  };

  const goBack = () => {
    if (globalSearchResults) clearGlobalSearch();
    if (isAtSharedBrowseRoot) {
      onExitSharedBrowse?.();
      return;
    }
    if (pathFolders.length > 1) {
      const parentId = pathFolders[pathFolders.length - 2]._id;
      setPathFolders((prev) => prev.slice(0, -1));
      setHistory((prev) => prev.slice(0, -1));
      fetchFolderData(parentId, { pathMode: 'none' });
    }
  };

  const navigateToBreadcrumb = (folderId: string, index: number) => {
    if (globalSearchResults) clearGlobalSearch();
    setPathFolders((prev) => prev.slice(0, index + 1));
    setHistory((prev) => prev.slice(0, index));
    fetchFolderData(folderId, { pathMode: 'none' });
  };

  const onDragStart = (e: React.DragEvent, fileId: string) => {
    if (!canEditFilesInFolder) return;
    setDraggedFileId(fileId);
    e.dataTransfer.setData('fileId', fileId);
    if (currentFolder?._id) e.dataTransfer.setData('sourceFolderId', currentFolder._id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent, folderId: string) => {
    if (viewMode === 'trash' || !canEditFilesInFolder) return;
    e.preventDefault();
    setDragOverFolderId(folderId);
    e.dataTransfer.dropEffect = 'move';
  };

  const onDragLeave = () => {
    setDragOverFolderId(null);
  };

  const onDrop = async (e: React.DragEvent, targetFolderId: string) => {
    if (viewMode === 'trash' || !canEditFilesInFolder) return;
    e.preventDefault();
    e.stopPropagation(); // prevent folder click (e.g. navigate) from firing
    setDragOverFolderId(null);

    const fileId = e.dataTransfer.getData('fileId');
    const sourceFolderId = e.dataTransfer.getData('sourceFolderId');

    if (fileId && targetFolderId && fileId !== targetFolderId) {
      // Skip if dropping into the same folder the file is already in
      if (sourceFolderId && sourceFolderId === targetFolderId) {
        setDraggedFileId(null);
        return;
      }
      try {
        await dmsApi.files.move(fileId, targetFolderId);
        toast({
          title: "File moved",
          description: "Document relocated successfully via drag and drop.",
        });
        refreshData();
      } catch (err: any) {
        toast({
          variant: "destructive",
          title: "Move failed",
          description: getFolderActionErrorMessage(err, 'Could not complete the move operation. File and folder must be in the same department.'),
        });
      }
    }
    setDraggedFileId(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !currentFolder) return;
    try {
      await dmsApi.folders.create({
        name: newFolderName,
        parentId: currentFolder._id,
        departmentId: currentFolder.departmentId,
        ...(newFolderIsPrivate ? { isPrivate: true } : {}),
      });
      toast({ title: "Folder created", description: `"${newFolderName}" was created successfully.` });
      setIsNewFolderOpen(false);
      setNewFolderName('');
      setNewFolderIsPrivate(false);
      refreshData();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: getFolderActionErrorMessage(err, 'Could not create folder.'),
      });
    }
  };

  const uploadBinaryWithProgress = (url: string, file: File, isLocal: boolean) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      
      const token = localStorage.getItem('dms_token');
      if (isLocal && token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('X-Upload-Token', url.split('local://')[1] || ''); // This is simplified, real logic uses the full presignedUrl as X-Upload-Token
      }
      
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(prev => prev ? { ...prev, percent } : null);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(file);
    });
  };

  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentFolder) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: 'Maximum upload size is 10 MB.',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (isDepartmentRootFolder) {
      toast({
        variant: 'destructive',
        title: 'Upload not allowed here',
        description: 'You can only upload files inside subfolders, not in department root.',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadProgress({ name: file.name, percent: 0 });

    try {
      const { fileId, presignedUrl } = await dmsApi.files.requestUpload({
        folderId: currentFolder._id,
        originalName: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
      });

      if (presignedUrl.startsWith('local://')) {
        // Step 2 Local: Upload to DMS API
        const uploadUrl = '/api/files/upload';
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        const token = localStorage.getItem('dms_token');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('X-Upload-Token', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress({ name: file.name, percent: Math.round((event.loaded / event.total) * 100) });
          }
        };

        await new Promise((resolve, reject) => {
          xhr.onload = () => (xhr.status === 204 ? resolve(true) : reject(new Error('Upload failed')));
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send(file);
        });
      } else {
        // Step 2 S3: Upload directly to S3 URL
        await uploadBinaryWithProgress(presignedUrl, file, false);
      }

      await dmsApi.files.confirmUpload(fileId);
      toast({ title: "File uploaded", description: `"${file.name}" was uploaded successfully.` });
      refreshData();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: getFolderActionErrorMessage(err, 'An unexpected error occurred.'),
      });
    } finally {
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRename = async () => {
    if (!renameItem || !renameValue.trim() || renameSubmitting) return;
    setRenameSubmitting(true);
    try {
      if (renameItem.type === 'folder') {
        await dmsApi.folders.rename(renameItem.id, renameValue);
      } else {
        await dmsApi.files.rename(renameItem.id, renameValue);
      }
      toast({ title: "Renamed", description: "Successfully updated name." });
      setIsRenameOpen(false);
      setRenameItem(null);
      setRenameValue('');
      clearBodyDialogStyles();
      refreshData(true);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: getFolderActionErrorMessage(err, 'Rename failed.'),
      });
    } finally {
      setRenameSubmitting(false);
    }
  };

  const parentFolderId = pathFolders.length >= 2 ? pathFolders[pathFolders.length - 2]._id : null;

  const handleMoveToParentFolder = async (fileId: string) => {
    if (!parentFolderId) return;
    setMoveSubmitting(fileId);
    try {
      await dmsApi.files.move(fileId, parentFolderId);
      toast({ title: "Moved", description: "File moved to parent folder." });
      refreshData();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Move failed',
        description: getFolderActionErrorMessage(err, 'File and folder must be in the same department.'),
      });
    } finally {
      setMoveSubmitting(null);
    }
  };

  const handleDownloadFile = async (fileId: string, filename?: string | null) => {
    try {
      await dmsApi.files.download(fileId, filename);
      toast({ title: "Download started", description: filename ? `"${filename}" is downloading.` : "File is downloading." });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Download failed", description: err?.message || "Could not download file." });
    }
  };

  const handleDeleteFolder = async (id: string) => {
    try {
      await dmsApi.folders.delete(id);
      toast({ title: "Folder deleted", description: "Item moved to trash." });
      refreshData();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: getFolderActionErrorMessage(err, 'Could not delete folder.'),
      });
    }
  };

  const handleDeleteFile = async (id: string) => {
    try {
      await dmsApi.files.delete(id);
      toast({ title: "File deleted", description: "Item moved to trash." });
      refreshData();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: getFolderActionErrorMessage(err, 'Could not delete file.'),
      });
    }
  };

  const handleRestoreFile = async (id: string) => {
    try {
      await dmsApi.files.restore(id);
      toast({ title: "File restored", description: "Item moved back to its location." });
      refreshData();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.error });
    }
  };

  const getFileIcon = (mimeType: string, size: number = 20) => {
    if (mimeType?.includes('pdf')) return <FileText size={size} className="text-red-500" />;
    if (mimeType?.includes('image')) return <FileImage size={size} className="text-blue-500" />;
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return <FileArchive size={size} className="text-orange-500" />;
    return <File size={size} className="text-gray-500" />;
  };

  const isPreviewable = (mimeType?: string) => {
    if (!mimeType) return true;
    const m = mimeType.toLowerCase();
    return (
      m.startsWith('image/') ||
      m.startsWith('video/') ||
      m.startsWith('audio/') ||
      m === 'application/pdf' ||
      m.startsWith('text/') ||
      m === 'application/json' ||
      m === 'application/xml' ||
      m === 'application/javascript' ||
      m === 'application/x-yaml' ||
      m === 'application/yaml' ||
      m.startsWith('application/vnd.openxmlformats') // Excel, Word, etc. - try iframe
    );
  };

  const openPreview = (file: { _id: string; originalName: string; mimeType?: string }) => {
    setPreviewFile(file);
  };

  const closePreview = () => {
    setPreviewFile(null);
    clearBodyDialogStyles();
  };

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (viewMode === 'trash') return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, visible: true });
  };

  if (isLoading && !currentFolder) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-primary w-8 h-8" />
      </div>
    );
  }

  const filteredFolders = children.folders.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredFiles = children.files.filter(f => (f.originalName || '').toLowerCase().includes(searchQuery.toLowerCase()));
  const inGlobalResultsMode = Boolean(globalSearchResults);
  const displayedFolders = inGlobalResultsMode ? (globalSearchResults?.folders ?? []) : filteredFolders;
  const displayedFiles = inGlobalResultsMode ? (globalSearchResults?.files ?? []) : filteredFiles;

  return (
    <div className="flex flex-col h-full bg-background relative">
      <input 
        type="file" 
        className="hidden" 
        ref={fileInputRef} 
        onChange={handleUpload}
      />

      <div className="flex items-center justify-between p-4 border-b bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack} disabled={!canGoBack}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-1 text-sm font-medium flex-wrap">
            {pathFolders.length === 0 ? (
              <span className="text-muted-foreground">No folder selected</span>
            ) : (
              pathFolders.map((folder, idx) => (
                <div key={folder._id} className="flex items-center gap-1">
                  {idx > 0 && <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />}
                  <span
                    className={cn(
                      "truncate max-w-[140px] sm:max-w-[200px]",
                      idx === pathFolders.length - 1
                        ? "font-bold text-primary cursor-default"
                        : "text-muted-foreground hover:text-primary cursor-pointer"
                    )}
                    onClick={() => idx < pathFolders.length - 1 && navigateToBreadcrumb(folder._id, idx)}
                    title={folder.name}
                  >
                    {folder.name}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64 mr-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input 
              placeholder="Search in folder..." 
              className="pl-9 h-9" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {viewMode === 'all' && !inSharedRecipientMode && (
            <div className="flex items-center gap-2 mr-2">
              <div className="relative w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  placeholder="Global search in department..."
                  className="pl-9 h-9"
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleGlobalSearch()}
                />
              </div>
              <Button variant="outline" size="sm" className="h-9" onClick={handleGlobalSearch} disabled={globalSearchLoading}>
                {globalSearchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>
              {(inGlobalResultsMode || globalSearchQuery) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  title="Clear global search"
                  onClick={() => {
                    setGlobalSearchQuery('');
                    clearGlobalSearch();
                  }}
                >
                  <X size={16} />
                </Button>
              )}
            </div>
          )}
          {viewMode === 'all' && (
            <div className="flex items-center gap-1 bg-secondary/30 p-1 rounded-lg">
              {canCreateSubfolder && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:bg-primary/10" title="New Folder" onClick={() => setIsNewFolderOpen(true)}>
                  <FolderPlus size={18} />
                </Button>
              )}
              {canUploadInCurrentFolder && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:bg-primary/10" title="Upload File" onClick={() => fileInputRef.current?.click()}>
                  <Upload size={18} />
                </Button>
              )}
            </div>
          )}
          {viewMode === 'all' &&
            currentFolder &&
            !isDepartmentRootFolder &&
            hasPermission('share_folder') &&
            canManageSharesOnResource(currentFolder) && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 h-9 gap-1.5"
                onClick={() =>
                  setShareTarget({ resourceType: 'folder', resourceId: currentFolder._id })
                }
              >
                <Share2 size={16} />
                Share folder
              </Button>
            )}
        </div>
      </div>

      {uploadProgress && (
        <div className="mx-6 mt-4 p-4 bg-primary/5 border border-primary/20 rounded-xl animate-in slide-in-from-top-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <Upload size={16} className="animate-bounce" />
              <span>Uploading {uploadProgress.name}...</span>
            </div>
            <span className="text-xs font-bold text-primary">{uploadProgress.percent}%</span>
          </div>
          <Progress value={uploadProgress.percent} className="h-2" />
        </div>
      )}

      <div 
        className="flex-1 overflow-auto p-6"
        onContextMenu={handleContextMenu}
      >
        {isLoading ? (
           <div className="flex items-center justify-center p-12">
             <Loader2 className="animate-spin text-primary w-6 h-6" />
           </div>
        ) : (
          <div className="min-h-full">
            {inGlobalResultsMode && (
              <div className="mb-3 px-1">
                <p className="text-xs text-muted-foreground">
                  Department-wide results for "<span className="font-medium">{globalSearchQuery.trim()}</span>"
                </p>
                {globalSearchError && (
                  <p className="text-xs text-destructive mt-1">{globalSearchError}</p>
                )}
              </div>
            )}
            {viewMode === 'all' && !inGlobalResultsMode && canEditFilesInFolder && (filteredFiles.length > 0 || filteredFolders.length > 0) && (
              <p className="text-xs text-muted-foreground mb-3 px-1">
                Drag files onto folders to move them.
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-6">
              {displayedFolders.map((folder) => (
                <div 
                  key={folder._id} 
                  className={cn(
                    "group relative flex flex-col items-center gap-2 p-4 rounded-xl transition-all cursor-pointer border border-transparent",
                    dragOverFolderId === folder._id ? "bg-primary/15 border-primary scale-105 shadow-lg ring-2 ring-primary/30" : "hover:bg-white hover:shadow-sm hover:border-border"
                  )}
                  onClick={() => inGlobalResultsMode ? fetchFolderData(folder._id, { pathMode: 'replace' }) : navigateTo(folder._id)}
                  onDragOver={(e) => !inGlobalResultsMode && onDragOver(e, folder._id)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => !inGlobalResultsMode && onDrop(e, folder._id)}
                >
                  <div className="w-full aspect-square bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform overflow-hidden">
                    <Folder size={48} fill="currentColor" className="opacity-80" />
                  </div>
                  <div className="text-center w-full">
                    <div className="text-sm font-medium truncate px-1">{folder.name}</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <Badge variant={folder.visibility === 'private' ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0 h-4 capitalize">
                        {folder.visibility === 'private' ? <Lock size={10} className="mr-1" /> : <Users size={10} className="mr-1" />}
                        {folder.visibility === 'private' ? 'Private' : 'Shared'}
                      </Badge>
                    </div>
                  </div>

                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {viewMode === 'all' &&
                    ( (hasPermission('share_folder') && canManageSharesOnResource(folder)) ||
                      canRenameFolder ||
                      showDeleteFolder) ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {hasPermission('share_folder') && canManageSharesOnResource(folder) && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareTarget({ resourceType: 'folder', resourceId: folder._id });
                            }}
                          >
                            <Share2 size={14} className="mr-2" />
                            Share
                          </DropdownMenuItem>
                        )}
                        {canRenameFolder && (
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            setRenameItem({ id: folder._id, name: folder.name, type: 'folder' });
                            setRenameValue(folder.name);
                            setIsRenameOpen(true);
                          }}>
                            <Pencil size={14} className="mr-2" />
                            Rename
                          </DropdownMenuItem>
                        )}
                        {showDeleteFolder && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder._id);
                            }}>
                              <Trash2 size={14} className="mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    ) : null}
                  </div>
                </div>
              ))}

              {displayedFiles.map((file) => (
                <div 
                  key={file._id} 
                  draggable={viewMode === 'all' && canEditFilesInFolder && !inGlobalResultsMode}
                  onDragStart={(e) => onDragStart(e, file._id)}
                  className={cn(
                    "group relative flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-border",
                    viewMode === 'all' && canEditFilesInFolder && "cursor-grab active:cursor-grabbing",
                    draggedFileId === file._id && "opacity-40 grayscale cursor-grabbing"
                  )}
                >
                  <div
                    className={cn(
                      "w-full aspect-square bg-accent/5 text-accent rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform overflow-hidden relative",
                      isPreviewable(file.mimeType) && "cursor-pointer"
                    )}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      if (isPreviewable(file.mimeType)) openPreview({ _id: file._id, originalName: file.originalName, mimeType: file.mimeType });
                    }}
                  >
                    {file.mimeType?.startsWith('image/') ? (
                      <div className="w-full h-full relative select-none">
                        <Image 
                          src={dmsApi.files.getFileViewUrl(file._id)} 
                          alt={file.originalName}
                          fill
                          className="object-cover"
                          unoptimized
                          draggable={false}
                        />
                      </div>
                    ) : (
                      getFileIcon(file.mimeType, 48)
                    )}
                  </div>
                  <div className="text-center w-full">
                    <div className="text-sm font-medium truncate px-1" title={file.originalName}>{file.originalName}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-tight">{formatSize(file.size)}</div>
                  </div>

                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {viewMode === 'trash' ? (
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-primary" onClick={() => handleRestoreFile(file._id)}>
                        <RotateCcw size={14} />
                      </Button>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical size={14} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isPreviewable(file.mimeType) && (
                            <DropdownMenuItem onClick={() => openPreview({ _id: file._id, originalName: file.originalName, mimeType: file.mimeType })}>
                              <Eye size={14} className="mr-2" />
                              Preview
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDownloadFile(file._id, file.originalName ?? (file as any).name)}>
                            <Download size={14} className="mr-2" />
                            Download
                          </DropdownMenuItem>
                          {hasPermission('share_file') && canManageSharesOnResource(file) && (
                            <DropdownMenuItem onClick={() => setShareTarget({ resourceType: 'file', resourceId: file._id })}>
                              <Share2 size={14} className="mr-2" />
                              Share
                            </DropdownMenuItem>
                          )}
                          {canEditFilesInFolder && (
                            <DropdownMenuItem onClick={() => {
                              setRenameItem({ id: file._id, name: file.originalName, type: 'file' });
                              setRenameValue(file.originalName);
                              setIsRenameOpen(true);
                            }}>
                              <Pencil size={14} className="mr-2" />
                              Rename
                            </DropdownMenuItem>
                          )}
                          {canEditFilesInFolder && (
                            <DropdownMenuItem
                              onClick={() => handleMoveToParentFolder(file._id)}
                              disabled={!parentFolderId || moveSubmitting === file._id}
                            >
                              {moveSubmitting === file._id ? (
                                <Loader2 size={14} className="mr-2 animate-spin" />
                              ) : (
                                <MoveHorizontal size={14} className="mr-2" />
                              )}
                              Move to parent folder
                            </DropdownMenuItem>
                          )}
                          {showDeleteFile && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteFile(file._id)}>
                                <Trash2 size={14} className="mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {displayedFolders.length === 0 && displayedFiles.length === 0 && (
              <div className="flex flex-col items-center justify-center p-20 text-muted-foreground">
                <div className="w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-4">
                  <Folder size={32} className="opacity-50" />
                </div>
                <p className="font-medium text-lg">
                  {inGlobalResultsMode ? 'No results found' : 'This folder is empty'}
                </p>
                <p className="text-sm mb-6">
                  {inGlobalResultsMode
                    ? 'Try another global search term.'
                    : 'Upload files or create folders to get started.'}
                </p>
                <div className="flex gap-3">
                   {!inGlobalResultsMode && canCreateSubfolder && (
                     <Button variant="outline" className="gap-2" onClick={() => setIsNewFolderOpen(true)}>
                       <FolderPlus size={16} />
                       New Folder
                     </Button>
                   )}
                   {!inGlobalResultsMode && canUploadInCurrentFolder && (
                     <Button className="gap-2 bg-primary" onClick={() => fileInputRef.current?.click()}>
                       <Upload size={16} />
                       Upload File
                     </Button>
                   )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {contextMenu && (
        <div 
          className="fixed z-50 bg-white border border-border rounded-md shadow-xl p-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-75"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground uppercase tracking-tight border-b mb-1">Actions</div>
          {canCreateSubfolder && (
            <button 
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary rounded transition-colors text-left"
              onClick={() => {
                setIsNewFolderOpen(true);
                setContextMenu(null);
              }}
            >
              <FolderPlus size={16} className="text-primary" />
              New Folder
            </button>
          )}
          {canUploadInCurrentFolder && (
            <button 
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary rounded transition-colors text-left"
              onClick={() => {
                fileInputRef.current?.click();
                setContextMenu(null);
              }}
            >
              <Upload size={16} className="text-primary" />
              Upload File
            </button>
          )}
          <div className="h-px bg-border my-1" />
          <button 
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary rounded transition-colors text-left"
            onClick={() => {
              refreshData();
              setContextMenu(null);
            }}
          >
            <RotateCcw size={16} className="text-muted-foreground" />
            Refresh
          </button>
        </div>
      )}

      <Dialog
        open={isNewFolderOpen}
        onOpenChange={(open) => {
          setIsNewFolderOpen(open);
          if (!open) setNewFolderIsPrivate(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name and choose whether this folder is private or shared.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input 
              id="folder-name" 
              value={newFolderName} 
              onChange={(e) => setNewFolderName(e.target.value)} 
              placeholder="e.g. Finance Reports"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-1">
                <Label htmlFor="folder-private-toggle" className="text-sm font-medium">
                  Private folder
                </Label>
                <p className="text-xs text-muted-foreground">
                  Private folders are visible only to owner unless shared.
                </p>
              </div>
              <Switch
                id="folder-private-toggle"
                checked={newFolderIsPrivate}
                onCheckedChange={setNewFolderIsPrivate}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setIsNewFolderOpen(false);
                setNewFolderIsPrivate(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isRenameOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsRenameOpen(false);
            if (!renameSubmitting) {
              setRenameItem(null);
              setRenameValue('');
            }
            clearBodyDialogStyles();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {renameItem?.type}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename-value">New Name</Label>
            <Input
              id="rename-value"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !renameSubmitting && handleRename()}
              disabled={renameSubmitting}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setIsRenameOpen(false);
                setRenameItem(null);
                setRenameValue('');
                clearBodyDialogStyles();
              }}
              disabled={renameSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={renameSubmitting || !renameValue.trim()}>
              {renameSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Renaming…
                </>
              ) : (
                'Rename'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Centered access error alert */}
      {centerError && (
        <Dialog open onOpenChange={(open) => { if (!open) setCenterError(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Access denied</DialogTitle>
            </DialogHeader>
            <div className="py-2 text-sm text-muted-foreground">
              {centerError}
            </div>
            <DialogFooter>
              <Button onClick={() => setCenterError(null)}>OK</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* File preview popup - only mount when open so overlay fully unmounts on close */}
      {shareTarget && (
        <ShareResourceDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setShareTarget(null);
              clearBodyDialogStyles();
            }
          }}
          resourceType={shareTarget.resourceType}
          resourceId={shareTarget.resourceId}
        />
      )}

      {previewFile && (
        <Dialog open={true} onOpenChange={(open) => { if (!open) closePreview(); }}>
          <DialogContent
            className="max-w-6xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0 z-[100] pointer-events-auto"
            onPointerDownOutside={closePreview}
            onEscapeKeyDown={closePreview}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
              <DialogTitle className="text-base font-medium truncate pr-4">
                {previewFile.originalName}
              </DialogTitle>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    handleDownloadFile(
                      previewFile._id,
                      previewFile.originalName ?? (previewFile as any).name
                    )
                  }
                >
                  <Download size={16} className="mr-1" />
                  Download
                </Button>
                <Button variant="ghost" size="icon" onClick={closePreview} aria-label="Close preview">
                  <X size={18} />
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto bg-muted/30 p-4 flex items-center justify-center">
              {(() => {
                const mime = (previewFile.mimeType || '').toLowerCase();
                const viewUrl = dmsApi.files.getFileViewUrl(previewFile._id);
                if (mime.startsWith('image/')) {
                  return (
                    <img
                      src={viewUrl}
                      alt={previewFile.originalName}
                      className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-lg"
                    />
                  );
                }
                if (mime.startsWith('video/')) {
                  return (
                    <video
                      src={viewUrl}
                      controls
                      className="max-w-full max-h-[75vh] rounded-lg shadow-lg"
                      preload="metadata"
                    />
                  );
                }
                if (mime.startsWith('audio/')) {
                  return (
                    <div className="w-full max-w-md">
                      <audio src={viewUrl} controls className="w-full" preload="metadata" />
                      <p className="text-sm text-muted-foreground mt-2 text-center">
                        {previewFile.originalName}
                      </p>
                    </div>
                  );
                }
                if (mime === 'application/pdf') {
                  return (
                    <iframe
                      src={viewUrl}
                      title={previewFile.originalName}
                      className="w-full h-[75vh] min-h-[400px] rounded-lg border bg-white"
                    />
                  );
                }
                if (
                  mime.startsWith('text/') ||
                  mime === 'application/json' ||
                  mime === 'application/xml' ||
                  mime === 'application/javascript' ||
                  mime === 'application/x-yaml' ||
                  mime === 'application/yaml'
                ) {
                  if (previewTextLoading) {
                    return (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 size={24} className="animate-spin" />
                        <span>Loading…</span>
                      </div>
                    );
                  }
                  if (previewTextContent !== null) {
                    return (
                      <pre className="w-full max-h-[75vh] overflow-auto text-left text-sm bg-background border rounded-lg p-4 shadow-inner font-mono whitespace-pre-wrap break-words">
                        <code>{previewTextContent}</code>
                      </pre>
                    );
                  }
                  return (
                    <p className="text-muted-foreground">Could not load file content.</p>
                  );
                }
                // Excel, Word, etc.: try iframe; fallback message
                if (
                  mime.includes('spreadsheet') ||
                  mime.includes('document') ||
                  mime.includes('openxmlformats')
                ) {
                  return (
                    <iframe
                      src={viewUrl}
                      title={previewFile.originalName}
                      className="w-full h-[75vh] min-h-[400px] rounded-lg border bg-white"
                    />
                  );
                }
                return (
                  <div className="text-center text-muted-foreground space-y-3">
                    <p>Preview not available for this file type.</p>
                    <Button
                      variant="outline"
                      onClick={() =>
                        handleDownloadFile(
                          previewFile._id,
                          previewFile.originalName ?? (previewFile as any).name
                        )
                      }
                    >
                      <Download size={16} className="mr-2" />
                      Download file
                    </Button>
                  </div>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
